import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Package, Receipt, Bicycle, Wrench, ChatCircle, ChartLine, House, SignOut, ArrowsClockwise, Key, LinkSimple, Buildings, ListBullets } from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: ChartLine, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: ListBullets },
  { to: "/admin/orders", label: "Orders", icon: Receipt },
  { to: "/admin/rentals", label: "Rentals", icon: Bicycle },
  { to: "/admin/services", label: "Services", icon: Wrench },
  { to: "/admin/messages", label: "Messages", icon: ChatCircle },
  { to: "/admin/import", label: "URL Import", icon: LinkSimple },
  { to: "/admin/suppliers", label: "Suppliers", icon: Buildings },
  { to: "/admin/feeds", label: "Feed Sync", icon: ArrowsClockwise },
  { to: "/admin/password-resets", label: "Password Resets", icon: Key },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) return <div className="p-12 text-center text-neutral-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-neutral-50 flex" data-testid="admin-layout">
      <aside className="w-64 bg-black text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2 no-underline-link text-white">
            <img src="https://customer-assets.emergentagent.com/job_ecommerce-veloteka/artifacts/3afh4xkv_logo%20veloteka.png" alt="VELOTEKA" className="h-8 w-8" />
            <div>
              <div className="font-display font-black text-lg leading-none">VELOTEKA</div>
              <div className="overline text-[color:var(--vt-orange)] text-[9px]">ADMIN</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-4">
          {NAV.map((item) => {
            const active = item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  active ? "bg-[color:var(--vt-orange)] text-black" : "text-neutral-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <item.icon size={18} weight={active ? "fill" : "regular"} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" className="flex items-center gap-2 text-xs text-neutral-400 hover:text-white">
            <House size={14} /> View Site
          </Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-neutral-400 hover:text-white" data-testid="admin-logout">
            <SignOut size={14} /> Logout
          </button>
          <div className="pt-2 border-t border-white/5 text-[10px] text-neutral-500">Logged in as<br /><strong className="text-white">{user.email}</strong></div>
        </div>
      </aside>
      <main className="flex-1 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
